// Copyright (c) Docugami, Inc. All rights reserved.

import { TooltipHost } from '@fluentui/react';
import { dgTagFontFamily } from 'components/common/Fonts';
import { emptyGray, primaryTheme } from 'components/shell/presentation/AppStyles';
import { MutableRefObject, useEffect, useImperativeHandle, useRef } from 'react';
import { Hint } from 'react-autocomplete-hint';
import styled from 'styled-components';

export interface IInputField {
    focus: () => void;
}

const InputRoot = styled.div<{ isValid?: boolean, isEditing?: boolean, fontSize?: string }>`
    width: 100%;
    input {
        width: 100%;
        font-family: ${dgTagFontFamily};
        border: hidden;
        outline: none;
        font-weight: bold;
        font-size: ${props => props.fontSize ?? '16px'};
        color: ${primaryTheme.palette.black};
        background-color: inherit;
        font-style: ${props => !!props.isEditing ? 'italic' : ''};
        padding: 0;

        // Overflow ellipsis behavior
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        ::placeholder {
            color: ${emptyGray};
            font-style: italic;
        }
    }
`;

const InputErrorText = styled.div`
    color: red;
    font-size: 9px;
    line-height: 10px;
`;

interface HintInputFieldProps {
    id: string;
    value: string;
    placeholder?: string;
    onChange?: (newValue: string) => void;
    isEditing?: boolean;
    onEdit?: () => void;
    isValid?: (value: string) => boolean;
    inputRef?: MutableRefObject<IInputField | undefined>;
    hints?: string[];
    onAccept?: () => void;
    onCancel?: () => void;
    maxLength?: number;
    onBlur?: () => void;
    getErrorText?: (value: string) => string;
    disabled?: boolean;
    className?: string;
    fontSize?: string;
}

export const HintInputField = (props: HintInputFieldProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(props.inputRef, () => ({
        focus: () => {
            inputRef.current?.focus();
        }
    }));

    useEffect(() => {
        if (!!props.isEditing) {
            inputRef.current?.focus();
        }
    }, [props.isEditing]);

    function isValid(value: string) {
        return !props.isValid || props.isValid(value);
    }

    return (
        <InputRoot isEditing={props.isEditing} className={props.className} fontSize={props.fontSize} >
            <TooltipHost content={!props.isEditing ? props.value : ''}>
                <Hint disableHint={!props.isEditing} options={props.hints ?? []} allowTabFill onFill={v => props.onChange?.(v as string)}>
                    <input
                        id={props.id}
                        ref={inputRef}
                        value={props.value}
                        disabled={props.disabled}
                        autoComplete='off'
                        placeholder={props.placeholder}
                        readOnly={!props.isEditing}
                        onChange={(e) => props.onChange?.(e.target.value)}
                        onClick={() => {
                            if (!props.isEditing) {
                                props.onEdit?.();
                            }
                        }}
                        onKeyDown={e => {
                            if (!!props.isEditing) {
                                if (e.key === "Enter" && isValid(props.value)) {
                                    props.onAccept?.();
                                }
                                else if (e.key === "Escape") {
                                    props.onCancel?.();
                                }
                            }
                        }}
                        maxLength={props.maxLength}
                        onBlur={props.onBlur} />
                </Hint>
            </TooltipHost>
            {!!props.getErrorText && <InputErrorText>{props.isValid?.(props.value) === false && !!props.isEditing ? props.getErrorText(props.value) : null}</InputErrorText>}
        </InputRoot>
    );
}
